import { Component, OnInit, NgZone } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { AlertController, LoadingController, ModalController, Platform, ToastController } from '@ionic/angular';
import { AuthenticationService } from '@app/core/services/authentication.service';
import { takeUntil } from 'rxjs/operators';
import { PushService } from '@app/core/services/push.service';
import { BankIdCollectResponseDTO } from 'ticket-sdk';
import { Capacitor, Plugins } from '@capacitor/core';
import { environment } from '@env';

let Keyboard;
if (Capacitor.platform !== 'web') {
  Keyboard = Plugins.Keyboard;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
})
export class LoginPage implements OnInit {
  keyboardVisible: boolean = false;
  isAuthenticated: Observable<boolean>;
  loadingSpinner: HTMLIonLoadingElement;
  modal: HTMLIonModalElement;
  name: string;
  redirectTo: string;

  constructor(
    public authService: AuthenticationService,
    public pushService: PushService,
    private router: Router,
    public loadingController: LoadingController,
    private route: ActivatedRoute,
    public toastController: ToastController,
    public platform: Platform,
    private zone: NgZone,
    private alertController: AlertController,
    public modalController: ModalController
    ) { }

  get isMobile() {
    return this.platform.is('mobile');
  }

  get isProd(): boolean {
    return environment.production;
  }

  get isWeb() {
    return ! this.platform.is('capacitor');
  }

  async ngOnInit() {
    this.isAuthenticated = this.authService.authenticationState;

    this.redirectTo = this.route.snapshot.paramMap.get('redirectTo');

    if (this.platform.is('capacitor')) {
      Keyboard.addListener('keyboardWillShow', () => {
        this.keyboardVisible = true
        this.zone.run((() => {}));
      });
  
      Keyboard.addListener('keyboardWillHide', () => {
        this.keyboardVisible = false;
        this.zone.run((() => {}));
      });
    }
  }

  async cancelLogin(loginSubject: Subject<any>, goToRegisterPage: boolean, message?: string) {
    this.authService.cancel();
    loginSubject.next(null);
    loginSubject.complete();
    await this.presentLoginErrorToast(message ? message : null);

    if (goToRegisterPage) {
      this.router.navigateByUrl('/register');
    }
  }

  getErrorMessageByHintCode(hintCode: BankIdCollectResponseDTO.HintCodeEnum): string {
    switch (hintCode) {
      case 'cancelled':
        return 'Avbruten'
      case 'certificateErr':
        return 'Problem med certifikat'
      case 'expiredTransaction':
      case 'startFailed':
        return 'Tiden för inloggningen löpte ut'
      case 'noClient':
        return 'BankID-klient saknas'
      case 'userCancel':
        return 'Inloggning avbruten av användaren'
      default:
        return 'Inloggningen misslyckades'
    }
  }

  async login(external: boolean) {
    const loginSubject = new Subject();
    
    this.authService.authenticate(external)
    .pipe(takeUntil(loginSubject))
    .subscribe(async collectResponse => {
      if (collectResponse) {
        switch (collectResponse.status) {
          case 'complete':
            this.onLoginComplete(collectResponse);
            loginSubject.next(null);
            loginSubject.complete();
            break;
          case 'unauthorized':
            this.cancelLogin(loginSubject, true, 'Du måste registrera ett konto för att kunna logga in');
            break;
          case 'failed':
            if (collectResponse.hintCode) {
              this.cancelLogin(loginSubject, false, this.getErrorMessageByHintCode(collectResponse.hintCode));
            }
            else {
              this.cancelLogin(loginSubject, false, 'Inloggningen misslyckades');
            }
            break;
          default:
            break;
        }
      }
      else {
        this.cancelLogin(loginSubject, false, 'Inloggningen misslyckades');
      }
    },
    async error => {
      if (error.status === 0) {
        this.cancelLogin(loginSubject, false, 'Kontrollera din internetanslutning och försök igen');
      }
      else if (error.message === 'Already in progress') {
        await this.presentLoginErrorToast('Du har redan påbörjat en inloggning. Prova igen om en stund.');
      }
      else {
        this.cancelLogin(loginSubject, false, 'Inloggningen misslyckades');
      }
    });
  }

  logout() {
    this.authService.logout().then(async () => {
      this.name = undefined;
    });
  }

  async presentLoginErrorToast(message?: string, duration?: number) {
    message = message ? message : 'Något gick fel. Prova igen om en stund';
    duration = duration ? duration : 5000;

    const toast = await this.toastController.create({
      message,
      color: 'danger',
      position: 'top',
      duration
    });
    toast.present();
  }

  async showUnauthenticatedPurchaseInfo() {
    const alert = await this.alertController.create({
      header: 'Biljettköp utan konto',
      message: 'Om du byter telefon eller rensar appens data så kommer du inte kunna se giltiga biljetter och historik.',
      buttons: [
        {
          text: 'OK',
          role: 'cancel'
        }
      ]
    });

    await alert.present();
  }

  onLoginComplete(collectResponse: BankIdCollectResponseDTO) {
    this.name = `${collectResponse.firstname} ${collectResponse.lastname}`;

    if (this.redirectTo) {
      this.router.navigate([this.redirectTo]);
    }
    else {
      this.router.navigateByUrl('/tickets');
    }
  }
}
